import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/react-hooks";
import { Box, Typography, Grid, Divider } from "@material-ui/core";
import {
  IntroText,
  PaperButton,
  FormActionDrawer,
  Loader,
  CurrencyToggle
} from "../../../components";
import { useTranslation } from "react-i18next";
import {
  SERVICE_COVID_19_TEST_MEXICO,
  SERVICE_COVID_19_TEST_MEXICO_ANTIGEN,
  SERVICE_COVID_19_TEST,
  SERVICE_COVID_19_TEST_ANTIGEN,
  SERVICE_COVID_19_TEST_KS,
  SERVICE_COVID_19_TEST_KS_ANTIGEN,
  COUNTRIES_CURRENCY,
  SERVICE_COVID_19_TEST_NV,
  SERVICE_COVID_19_TEST_NV_ANTIGEN,
  CURRENCIES,
  CURRENCY_USD,
  CURRENCY_MXN,
  SERVICE_COVID_19_TEST_MX_RESORT
} from "../../../constants";
import { GET_ALL_SERVICES } from "../../../queries";
import { isTestService } from "../../../utils";

const AVAILABLE_SERVICES = [
  {
    title: "PCR",
    service_code: SERVICE_COVID_19_TEST_MEXICO,
    recommended: true,
    summary_translation_key: [
      "schedule.covid_19_test_type.available_services.pcr.summary"
    ],
    default_currency: CURRENCY_MXN
  },
  {
    title: "Rapid Antigen",
    service_code: SERVICE_COVID_19_TEST_MEXICO_ANTIGEN,
    recommended: false,
    summary_translation_key: [
      "schedule.covid_19_test_type.available_services.antigen.summary"
    ],
    default_currency: CURRENCY_MXN
  },
  {
    title: "PCR",
    service_code: SERVICE_COVID_19_TEST,
    recommended: true,
    summary_translation_key: [
      "schedule.covid_19_test_type.available_services.pcr.summary_2",
      "schedule.covid_19_test_type.available_services.pcr.summary_3"
    ],
    default_currency: CURRENCY_USD
  },
  {
    title: "Rapid Antigen",
    service_code: SERVICE_COVID_19_TEST_ANTIGEN,
    recommended: false,
    summary_translation_key: [
      "schedule.covid_19_test_type.available_services.antigen.summary_2",
      "schedule.covid_19_test_type.available_services.antigen.summary_3",
      "schedule.covid_19_test_type.available_services.antigen.summary_4"
    ],
    default_currency: CURRENCY_USD
  },
  {
    title: "PCR",
    service_code: SERVICE_COVID_19_TEST_KS,
    recommended: true,
    summary_translation_key: [
      "schedule.covid_19_test_type.available_services.pcr.summary_2",
      "schedule.covid_19_test_type.available_services.pcr.summary_3"
    ],
    default_currency: CURRENCY_USD
  },
  {
    title: "Rapid Antigen",
    service_code: SERVICE_COVID_19_TEST_KS_ANTIGEN,
    recommended: false,
    summary_translation_key: [
      "schedule.covid_19_test_type.available_services.antigen.summary_2",
      "schedule.covid_19_test_type.available_services.antigen.summary_3",
      "schedule.covid_19_test_type.available_services.antigen.summary_4"
    ],
    default_currency: CURRENCY_USD
  },
  {
    title: "PCR",
    service_code: SERVICE_COVID_19_TEST_NV,
    recommended: true,
    summary_translation_key: [
      "schedule.covid_19_test_type.available_services.pcr.summary"
    ],
    default_currency: CURRENCY_USD
  },
  {
    title: "Rapid Antigen",
    service_code: SERVICE_COVID_19_TEST_NV_ANTIGEN,
    recommended: false,
    summary_translation_key: [
      "schedule.covid_19_test_type.available_services.antigen.summary"
    ],
    default_currency: CURRENCY_USD
  },
  {
    title: "PCR",
    service_code: SERVICE_COVID_19_TEST_MX_RESORT,
    recommended: true,
    summary_translation_key: [
      "schedule.covid_19_test_type.available_services.pcr.summary"
    ],
    default_currency: CURRENCY_MXN
  }
];

/**
 * Filter in Covid-19 Services that are available at the location
 * Merge hard-coded service title/summary with available services
 *
 * @param {array} allServices
 */
const availableServices = (allServices, locationServices) => {
  return allServices
    .filter(service => isTestService([service.service_code]))
    .filter(service =>
      locationServices.some(
        locationService => locationService.service_code === service.service_code
      )
    )
    .filter(service =>
      AVAILABLE_SERVICES.some(
        availableService =>
          availableService.service_code === service.service_code
      )
    )
    .map(service => {
      const [availableService] = AVAILABLE_SERVICES.filter(
        availableService =>
          availableService.service_code === service.service_code
      );
      return { ...service, ...availableService };
    });
};

const ScheduleCovid19TestType = ({
  updateScheduleData,
  scheduleData,
  isSubmitting,
  setDrawerClearance
}) => {
  const { t } = useTranslation();

  const [selectedTestType, setSelectedTestType] = useState();
  const [services, setServices] = useState([]);
  const [currency, setCurrency] = useState();
  const [availableCurrencies, setAvailableCurrencies] = useState([]);

  const { data, loading } = useQuery(GET_ALL_SERVICES, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!scheduleData || !scheduleData.country) return;

    COUNTRIES_CURRENCY[scheduleData.country] &&
      setCurrency(COUNTRIES_CURRENCY[scheduleData.country]);
  }, [scheduleData]);

  /**
   * Set services
   */
  useEffect(() => {
    if (
      !data ||
      !data.availableServices ||
      !data.availableServices.results ||
      !Array.isArray(data.availableServices.results.result) ||
      !Array.isArray(scheduleData.locationServices)
    )
      return;

    setServices(
      availableServices(
        data.availableServices.results.result,
        scheduleData.locationServices
      )
    );
  }, [data, scheduleData]);

  /**
   * Set available currencies based on currencies in available services
   */
  useEffect(() => {
    if (services.length <= 0) return;

    let serviceCurrencies = [];

    services.forEach(service => {
      Object.keys(service.self_pay_amount).forEach(currency => {
        if (!serviceCurrencies.includes(currency))
          serviceCurrencies = [...serviceCurrencies, currency];
      });
    });

    if (serviceCurrencies.length > 0) {
      setAvailableCurrencies(
        CURRENCIES.filter(currency => serviceCurrencies.includes(currency.abbr))
      );
    }
  }, [services]);

  /**
   * Get currency (either selected in currency toggle, or default currency from service)
   *
   * @param {string} selectedServiceCode
   * @returns
   */
  const getServiceCurrency = selectedServiceCode => {
    let theCurrency = CURRENCY_USD.abbr;

    if (currency && currency.abbr) {
      theCurrency = currency.abbr;
    } else {
      const [service] = AVAILABLE_SERVICES.filter(
        service => service.service_code === selectedServiceCode
      );

      if (service.default_currency && service.default_currency.abbr) {
        theCurrency = service.default_currency.abbr;
      }
    }

    return theCurrency;
  };

  const handleNext = () => {
    updateScheduleData({
      selectedServices: [selectedTestType],
      currency: getServiceCurrency(selectedTestType)
    });
  };

  if (loading) return <Loader />;

  return (
    <Box data-cy="schedule-screen-covid19-test-type">
      <IntroText>{t("schedule.covid_19_test_type.intro_text")}</IntroText>
      {availableCurrencies.length > 1 && (
        <Box mb={1}>
          <Grid container spacing={1} justify="flex-end" alignItems="center">
            <Grid item>
              <CurrencyToggle
                currenciesList={availableCurrencies}
                selectedCurrency={currency}
                onChange={(e, currency) => setCurrency(currency)}
                buttonProps={{ variant: "contained" }}
              />
            </Grid>
          </Grid>
        </Box>
      )}
      {services.length > 0 ? (
        <Grid container direction="column" spacing={2}>
          {services.map((service, index) => (
            <Grid item xs={12} key={`service-${service.service_code}`}>
              <PaperButton
                onClick={() => setSelectedTestType(service.service_code)}
                selected={selectedTestType === service.service_code}
                alertText={
                  service.recommended
                    ? t("schedule.covid_19_test_type.recommended")
                    : ""
                }
                data-cy={`button-${index}`}
              >
                <Typography
                  variant="h1"
                  component="h2"
                  gutterBottom
                  color="primary"
                  style={{ fontSize: "30px" }}
                >
                  {service.title}
                </Typography>
                <Typography paragraph variant="h4" component="p">
                  {currency && currency.abbr in service.self_pay_amount ? (
                    <>
                      {currency.symbol}
                      {service.self_pay_amount[currency.abbr]}
                    </>
                  ) : service.default_currency &&
                    service.default_currency.abbr in service.self_pay_amount ? (
                    <>
                      {service.default_currency.symbol}
                      {service.self_pay_amount[service.default_currency.abbr]}
                    </>
                  ) : (
                    t("schedule.covid_19_test_type.no_price")
                  )}
                </Typography>
                {Array.isArray(service.summary_translation_key) &&
                  service.summary_translation_key.length > 0 && (
                    <>
                      <Box mb={2}>
                        <Divider variant="middle" />
                      </Box>
                      <Box textAlign="center" px={2}>
                        {service.summary_translation_key.map(
                          (summary_translation_key, index) => (
                            <Typography key={index} variant="body1" paragraph>
                              {t(summary_translation_key)}
                            </Typography>
                          )
                        )}
                      </Box>
                    </>
                  )}
              </PaperButton>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography color="error">
          {t("schedule.covid_19_test_type.error_empty")}
        </Typography>
      )}
      {selectedTestType && (
        <FormActionDrawer
          setDrawerClearance={setDrawerClearance}
          handleNext={() => handleNext()}
          loading={isSubmitting}
        />
      )}
    </Box>
  );
};

export { ScheduleCovid19TestType };
